import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, addDays as dateFnsAddDays, startOfWeek as dateFnsStartOfWeek, endOfWeek as dateFnsEndOfWeek, startOfMonth as dateFnsStartOfMonth, endOfMonth as dateFnsEndOfMonth, eachDayOfInterval as dateFnsEachDayOfInterval, differenceInDays as dateFnsDifferenceInDays } from 'date-fns';

/**
 * Combines Tailwind classes with proper merging
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date to Australian format (DD/MM/YYYY)
 */
export function formatDate(date: Date | string | number): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return format(d, 'dd/MM/yyyy');
}

/**
 * Format date with time to Australian format
 */
export function formatDateTime(date: Date | string | number): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return format(d, 'dd/MM/yyyy HH:mm');
}

/**
 * Format currency to Australian Dollars
 */
export function formatCurrency(
  amount: number,
  options: { showSymbol?: boolean; decimals?: number } = {}
): string {
  const { showSymbol = true, decimals = 2 } = options;
  
  const formatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const formatted = formatter.format(amount);
  
  if (!showSymbol) {
    return formatted.replace('$', '').trim();
  }
  
  return formatted;
}

/**
 * Format ABN (Australian Business Number)
 * Input: 12123123123 or 12 123 123 123
 * Output: 12 123 123 123
 */
export function formatABN(abn: string | null | undefined): string {
  if (!abn) return '';
  
  // Remove all non-digit characters
  const digits = abn.replace(/\D/g, '');
  
  if (digits.length !== 11) {
    return abn; // Return as-is if not valid length
  }
  
  // Format as XX XXX XXX XXX
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
}

/**
 * Format ACN (Australian Company Number)
 * Input: 123123123 or 123 123 123
 * Output: 123 123 123
 */
export function formatACN(acn: string | null | undefined): string {
  if (!acn) return '';
  
  const digits = acn.replace(/\D/g, '');
  
  if (digits.length !== 9) {
    return acn;
  }
  
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
}

/**
 * Format phone number to Australian format
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  
  const digits = phone.replace(/\D/g, '');
  
  // Mobile: 04XX XXX XXX
  if (digits.startsWith('04') && digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
  }
  
  // Landline: (0X) XXXX XXXX
  if (digits.startsWith('0') && digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)} ${digits.slice(6, 10)}`;
  }
  
  // 1300/1800 numbers: 1XXX XXX XXX
  if ((digits.startsWith('1300') || digits.startsWith('1800')) && digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
  }
  
  return phone;
}

/**
 * Validate ABN using checksum algorithm
 */
export function validateABN(abn: string): boolean {
  const digits = abn.replace(/\D/g, '');
  
  if (digits.length !== 11) return false;
  
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const digits_array = digits.split('').map(Number);
  
  // Subtract 1 from first digit
  digits_array[0] -= 1;
  
  // Calculate sum
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    sum += digits_array[i] * weights[i];
  }
  
  return sum % 89 === 0;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Generate a random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(date: Date | string | number): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
}

/**
 * Format file size to human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  return dateFnsAddDays(date, days);
}

/**
 * Get start of week
 */
export function startOfWeek(date: Date, options?: { weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 }): Date {
  return dateFnsStartOfWeek(date, options);
}

/**
 * Get end of week
 */
export function endOfWeek(date: Date, options?: { weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 }): Date {
  return dateFnsEndOfWeek(date, options);
}

/**
 * Get start of month
 */
export function startOfMonth(date: Date): Date {
  return dateFnsStartOfMonth(date);
}

/**
 * Get end of month
 */
export function endOfMonth(date: Date): Date {
  return dateFnsEndOfMonth(date);
}

/**
 * Get each day in interval
 */
export function eachDayOfInterval(interval: { start: Date; end: Date }): Date[] {
  return dateFnsEachDayOfInterval(interval);
}

/**
 * Get difference in days between two dates
 */
export function differenceInDays(dateLeft: Date, dateRight: Date): number {
  return dateFnsDifferenceInDays(dateLeft, dateRight);
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(dateLeft: Date, dateRight: Date): boolean {
  return dateLeft.getDate() === dateRight.getDate() &&
    dateLeft.getMonth() === dateRight.getMonth() &&
    dateLeft.getFullYear() === dateRight.getFullYear();
}

/**
 * Check if two dates are in the same month
 */
export function isSameMonth(dateLeft: Date, dateRight: Date): boolean {
  return dateLeft.getMonth() === dateRight.getMonth() &&
    dateLeft.getFullYear() === dateRight.getFullYear();
}

/**
 * Format duration in milliseconds to human readable format
 */
export { format } from 'date-fns';

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.floor(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m`;
  return `${Math.floor(ms / 3600000)}h`;
}
